/**
 * Route configuration table.
 *
 * Maps hash paths to route parameters: section, tab, fn (screen registry key).
 *
 * Also exports helpers to convert between NavigateOptions and hash paths.
 */

import type { NavigateOptions } from '@/services/navigation';

export interface RouteConfig {
  /** Hash path pattern, e.g. "/:section/in-progress". Leading slash, no hash. */
  path: string;
  /** Section name: CAD, MDT, ADMIN, PAYMENT, Home */
  section: string | null;
  /** Tab identifier (used for menu highlighting, toolbar selection) */
  tab: string | null;
  /** Screen registry key. May contain `:section` placeholder replaced at match time. */
  fn: string | null;
  /** If true, section comes from URL param rather than being fixed */
  sectionFromParam?: boolean;
  /** Special handler type instead of normal screen registry flow */
  special?: 'home' | 'signout' | 'registration' | 'resetPassword' | 'unsubscribe';
}

// ── Route Table ─────────────────────────────────────────────────────────

export const routes: RouteConfig[] = [
  // Special routes
  { path: '/', section: null, tab: null, fn: null, special: 'login' },
  { path: '/home', section: 'Home', tab: null, fn: null, special: 'home' },
  { path: '/signout', section: null, tab: null, fn: null, special: 'signout' },
  { path: '/registration/:code', section: null, tab: null, fn: null, special: 'registration' },
  { path: '/reset-password/:code', section: null, tab: null, fn: null, special: 'resetPassword' },
  { path: '/unsubscribe', section: null, tab: null, fn: null, special: 'unsubscribe' },

  // Module home (Selected → triggers module selection callback)
  { path: '/:section/home', section: null, tab: 'Home', fn: ':SECTION_Home', sectionFromParam: true },

  // Core tabs
  { path: '/:section/in-progress', section: null, tab: 'InProgress', fn: ':SECTION_InProgress', sectionFromParam: true },
  { path: '/:section/in-progress-pending-fullscreen', section: null, tab: 'InProgressPendingCardsFullScreen', fn: ':SECTION_InProgressPendingCardsFullScreen', sectionFromParam: true },
  { path: '/:section/in-progress-active-fullscreen', section: null, tab: 'InProgressActiveCardsFullScreen', fn: ':SECTION_InProgressActiveCardsFullScreen', sectionFromParam: true },
  { path: '/:section/in-progress-closed-fullscreen', section: null, tab: 'InProgressClosedCardsFullScreen', fn: ':SECTION_InProgressClosedCardsFullScreen', sectionFromParam: true },
  { path: '/:section/in-progress-panic', section: null, tab: 'InProgressPanic', fn: ':SECTION_InProgressPanic', sectionFromParam: true },
  { path: '/:section/officer-status', section: null, tab: 'OfficerStatus', fn: ':SECTION_OfficerStatus', sectionFromParam: true },

  // Call Card
  { path: '/:section/call-card', section: null, tab: 'CallCard', fn: ':CALLCARD_FN', sectionFromParam: true },
  { path: '/:section/call-card/:id', section: null, tab: null, fn: ':CALLCARD_FN', sectionFromParam: true },

  // Case Reports
  { path: '/:section/case-reports', section: null, tab: 'CaseReports', fn: ':SECTION_CaseReports', sectionFromParam: true },
  { path: '/:section/case-report', section: null, tab: 'CaseReports', fn: 'displayCaseReport', sectionFromParam: true },
  { path: '/:section/case-report/:params', section: null, tab: 'CaseReports', fn: 'displayCaseReport', sectionFromParam: true },

  // Citations
  { path: '/:section/citations', section: null, tab: 'Citations', fn: ':SECTION_Citations', sectionFromParam: true },
  { path: '/:section/citation/:params', section: null, tab: 'Citations', fn: 'Open_CitationForm', sectionFromParam: true },

  // Search / NPSD
  { path: '/:section/search', section: null, tab: 'Search', fn: ':SECTION_Search', sectionFromParam: true },
  { path: '/:section/person', section: null, tab: 'Search', fn: 'Open_Person', sectionFromParam: true },
  { path: '/:section/person/:id', section: null, tab: 'Search', fn: 'Open_Person', sectionFromParam: true },
  { path: '/:section/vehicle', section: null, tab: 'Search', fn: 'Open_Vehicle', sectionFromParam: true },
  { path: '/:section/vehicle/:id', section: null, tab: 'Search', fn: 'Open_Vehicle', sectionFromParam: true },
  { path: '/:section/business', section: null, tab: 'Search', fn: 'Open_Business', sectionFromParam: true },
  { path: '/:section/business/:id', section: null, tab: 'Search', fn: 'Open_Business', sectionFromParam: true },
  { path: '/:section/client/:id', section: null, tab: 'Search', fn: 'Open_ClientGroup', sectionFromParam: true },
  { path: '/:section/tour-stop-completion/:id', section: null, tab: 'Search', fn: 'TourStopCompletion', sectionFromParam: true },

  // Address Review — supervisor QA screen, lives in the operational modules (CAD/MDT)
  // alongside case-report approval rather than admin, so Open/New Window on call cards
  // stay within the supervisor's selected module.
  { path: '/:section/address-review', section: null, tab: 'AddressReview', fn: ':SECTION_AddressReview', sectionFromParam: true },

  // Map
  { path: '/:section/map', section: null, tab: 'Map', fn: ':SECTION_Map', sectionFromParam: true },
  // map-by-address and map-by-id now use query params: /:section/map?address=... or /:section/map?callCardId=...

  // Common tabs
  { path: '/:section/log', section: null, tab: 'Log', fn: ':SECTION_Log', sectionFromParam: true },
  { path: '/:section/notes', section: null, tab: 'Notes', fn: ':SECTION_Notes', sectionFromParam: true },
  { path: '/:section/messaging', section: null, tab: 'Messaging', fn: ':SECTION_Messaging', sectionFromParam: true },
  { path: '/:section/user-profile', section: null, tab: 'UserProfile', fn: ':SECTION_UserProfile', sectionFromParam: true },
  { path: '/:section/user-profile/:id', section: null, tab: 'UserProfile', fn: ':SECTION_UserProfile', sectionFromParam: true },
  { path: '/:section/guard-tours', section: null, tab: 'GuardToursNew', fn: ':SECTION_GuardToursNew', sectionFromParam: true },
  { path: '/:section/guard-tours/:guardTourId/stops/:guardTourStopId', section: null, tab: 'GuardToursNew', fn: 'showCompleteTourStopsForGuardTour', sectionFromParam: true },
  { path: '/:section/activity-reports', section: null, tab: 'ActivityReport', fn: ':SECTION_ActivityReport', sectionFromParam: true },
  { path: '/:section/analytics-reports', section: null, tab: 'AnalyticsReport', fn: ':SECTION_AnalyticsReport', sectionFromParam: true },
  { path: '/:section/company-documents', section: null, tab: 'CompanyDocuments', fn: ':SECTION_CompanyDocuments', sectionFromParam: true },
  { path: '/:section/emergency-lookup', section: null, tab: 'EmergencyLookup', fn: ':SECTION_EmergencyLookup', sectionFromParam: true },
  { path: '/:section/custom-location-management', section: null, tab: 'CustomLocationManagement', fn: ':SECTION_CustomLocationManagement', sectionFromParam: true },
  { path: '/:section/call-alert-management', section: null, tab: 'CallAlertManagement', fn: ':SECTION_CallAlertManagement', sectionFromParam: true },
  // Advisory configuration (Types + Audit Log) is ADMIN-only (explicit /admin/advisory-management route below).
  // Operational advisory screen — single adaptive surface resolved on both CAD and MDT.
  { path: '/:section/advisories', section: null, tab: 'Advisories', fn: ':SECTION_Advisories', sectionFromParam: true },

  // SOP (opened in new window from call card)
  { path: '/:section/sop/:params', section: null, tab: 'InProgress', fn: ':SECTION_CallCardSop', sectionFromParam: true },

  // Admin-only routes
  { path: '/admin/user-management', section: 'ADMIN', tab: 'UserManagement', fn: 'ADMIN_UserManagement' },
  { path: '/admin/permissions', section: 'ADMIN', tab: 'Permissions', fn: 'ADMIN_Permissions' },
  { path: '/admin/tour-management', section: 'ADMIN', tab: 'TourManagement', fn: 'ADMIN_TourManagement' },
  { path: '/admin/tour-management/:subTab', section: 'ADMIN', tab: 'TourManagement', fn: 'ADMIN_TourManagement' },
  { path: '/admin/nature-of-call-management', section: 'ADMIN', tab: 'NOCManagement', fn: 'ADMIN_NOCManagement' },
  { path: '/admin/company-profile', section: 'ADMIN', tab: 'CompanyProfile', fn: 'ADMIN_CompanyProfile' },
  { path: '/admin/company-settings', section: 'ADMIN', tab: 'CompanySettings', fn: 'ADMIN_CompanySettings' },
  { path: '/admin/company-link', section: 'ADMIN', tab: 'CompanyLink', fn: 'ADMIN_CompanyLink' },
  { path: '/admin/call-card-management', section: 'ADMIN', tab: 'CallCardManagement', fn: 'ADMIN_CallCardManagement' },
  { path: '/admin/call-alert-management', section: 'ADMIN', tab: 'CallAlertManagement', fn: 'ADMIN_CallAlertManagement' },
  { path: '/admin/advisory-management', section: 'ADMIN', tab: 'AdvisoryManagement', fn: 'ADMIN_AdvisoryManagement' },
  { path: '/admin/call-sign-management', section: 'ADMIN', tab: 'CallSignManagement', fn: 'ADMIN_CallSignManagement' },
  { path: '/admin/status-update-management', section: 'ADMIN', tab: 'StatusUpdateManagement', fn: 'ADMIN_StatusUpdateManagement' },
  { path: '/admin/custom-location-management', section: 'ADMIN', tab: 'CustomLocationManagement', fn: 'ADMIN_CustomLocationManagement' },
  { path: '/admin/case-report-management', section: 'ADMIN', tab: 'CaseReportManagement', fn: 'ADMIN_CaseReportManagement' },
  { path: '/admin/citation-management', section: 'ADMIN', tab: 'CitationManagement', fn: 'ADMIN_CitationManagement' },
  { path: '/admin/vehicle-management', section: 'ADMIN', tab: 'VehicleManagement', fn: 'ADMIN_VehicleManagement' },
  { path: '/admin/client-group-management', section: 'ADMIN', tab: 'ClientGroupManagement', fn: 'ADMIN_ClientGroupManagement' },
  { path: '/admin/client-group', section: 'ADMIN', tab: 'ClientGroupManagement', fn: 'Open_AdminClientGroup' },
  { path: '/admin/client-group/:id', section: 'ADMIN', tab: 'ClientGroupManagement', fn: 'Open_AdminClientGroup' },
  { path: '/admin/client-portal', section: 'ADMIN', tab: 'ClientPortalAdmin', fn: 'ADMIN_ClientPortal' },
  { path: '/admin/map-overlays', section: 'ADMIN', tab: 'MapOverlayKml', fn: 'ADMIN_MapOverlayKml' },
  { path: '/admin/support-attachments', section: 'ADMIN', tab: 'OrnaSupportAttachments', fn: 'ADMIN_OrnaSupportAttachments' },
  { path: '/admin/sop-management', section: 'ADMIN', tab: 'Sop', fn: 'ADMIN_Sop' },
  { path: '/admin/activity-report-management', section: 'ADMIN', tab: 'ActivityReportManagement', fn: 'ADMIN_ActivityReportManagement' },
  { path: '/admin/custom-forms',  section: 'ADMIN', tab: 'CustomFormsManagement',  fn: 'ADMIN_CustomFormsManagement' },

  // Payment (accessible from admin toolbar as well)
  { path: '/payment/management', section: 'PAYMENT', tab: 'PaymentManagement', fn: 'PAYMENT_PaymentManagement' },
  { path: '/admin/payment-management', section: 'ADMIN', tab: 'PaymentManagement', fn: 'ADMIN_PaymentManagement' },

  // Registration / Reset / Unsubscribe (alternate forms)
  { path: '/registration', section: null, tab: null, fn: null, special: 'registration' },
  { path: '/reset-password', section: null, tab: null, fn: null, special: 'resetPassword' },
];

// ── Path matching ──────────────────────────────────────────────────────

interface MatchResult {
  route: RouteConfig;
  params: Record<string, string>;
}

/**
 * Match a hash path (e.g. "/cad/in-progress") against the route table.
 * Returns the matched route and extracted params, or null if no match.
 */
export function matchRoute(hashPath: string): MatchResult | null {
  // Normalize: ensure leading slash, strip trailing slash
  let path = hashPath.startsWith('/') ? hashPath : '/' + hashPath;
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

  for (const route of routes) {
    const params = matchPath(route.path, path);
    if (params !== null) {
      return { route, params };
    }
  }
  return null;
}

function matchPath(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split('/');
  const pathParts = path.split('/');

  // Check if lengths match (patterns with no optional params)
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i++) {
    const pat = patternParts[i];
    const val = pathParts[i];

    if (pat.startsWith(':')) {
      // Parameter segment
      params[pat.slice(1)] = val;
    } else if (pat.toLowerCase() !== val.toLowerCase()) {
      // Literal segment — case-insensitive match
      return null;
    }
  }
  return params;
}

/**
 * Resolve a route's section and fn given matched URL params.
 */
export function resolveRoute(route: RouteConfig, params: Record<string, string>): {
  section: string;
  tab: string | null;
  fn: string | null;
} {
  const sectionRaw = route.sectionFromParam ? params.section : route.section!;
  const section = sectionRaw.toUpperCase();

  let fn = route.fn;
  if (fn) {
    // Replace :SECTION placeholder
    fn = fn.replace(':SECTION', section);

    // Special call card fn logic: CAD uses CAD_CallCard, others use MDT_ShowCallCard
    if (fn === ':CALLCARD_FN') {
      fn = section === 'CAD' ? 'CAD_CallCard' : 'MDT_ShowCallCard';
    }
  }

  return {
    section,
    tab: route.tab,
    fn,
  };
}

// ── Hash path building ─────────────────────────────────────────────────

/** Section name to URL segment */
function sectionToSlug(section: string): string {
  return section.toLowerCase();
}

/** Tab name to URL slug */
const tabSlugs: Record<string, string> = {
  Home: 'home',
  InProgress: 'in-progress',
  OfficerStatus: 'officer-status',
  NewCallCard: 'call-card',
  CaseReports: 'case-reports',
  Citations: 'citations',
  Search: 'search',
  Map: 'map',
  Log: 'log',
  Notes: 'notes',
  Messaging: 'messaging',
  UserProfile: 'user-profile',
  GuardToursNew: 'guard-tours',
  ActivityReport: 'activity-reports',
  AnalyticsReport: 'analytics-reports',
  CompanyDocs: 'company-documents',
  EmergencyLookup: 'emergency-lookup',
  UserManagement: 'user-management',
  Permissions: 'permissions',
  TourManagement: 'tour-management',
  NocManagement: 'nature-of-call-management',
  CompanyProfile: 'company-profile',
  CompanySettings: 'company-settings',
  CompanyLink: 'company-link',
  CallCardManagement: 'call-card-management',
  CallAlertManagement: 'call-alert-management',
  AdvisoryManagement: 'advisory-management',
  CallSignManagement: 'call-sign-management',
  StatusUpdateManagement: 'status-update-management',
  CustomLocationManagement: 'custom-location-management',
  CaseReportManagement: 'case-report-management',
  CitationManagement: 'citation-management',
  VehicleManagement: 'vehicle-management',
  ClientGroupManagement: 'client-group-management',
  ClientPortalAdmin: 'client-portal',
  MapOverlayKml: 'map-overlays',
  OrnaSupportAttachments: 'support-attachments',
  Sop: 'sop-management',
  PaymentManagement: 'management',
  ActivityReportManagement: 'activity-report-management',
  CustomFormsManagement: 'custom-forms',
  AddressReview: 'address-review',
  SelfInitiate: 'call-card', // MDT self-initiate maps to call-card route
  InProgressPendingCardsFullScreen: 'in-progress-pending-fullscreen',
  InProgressActiveCardsFullScreen: 'in-progress-active-fullscreen',
  InProgressClosedCardsFullScreen: 'in-progress-closed-fullscreen',
  InProgressPanic: 'in-progress-panic',
  // Alternate names used by legacy URLs
  ClientPortal: 'client-portal',
  NOCManagement: 'nature-of-call-management',
  CompanyDocuments: 'company-documents',
  CallCard: 'call-card',
};

/**
 * Convert NavigateOptions → hash path (without the # prefix).
 *
 * This is the inverse of matchRoute + resolveRoute.
 */
export function buildHashPath(opts: NavigateOptions): string {
  const { section, tab, fn, multiModule, params } = opts;

  // Special cases
  if (section === 'Home') return '/home';

  const slug = sectionToSlug(section);

  // If no tab, just the section home
  if (!tab) return `/${slug}/home`;

  // Determine the page-level slug
  let pageSlug: string;

  // Check for specific fn-based routes that don't follow the tab pattern
  if (fn === 'displayCaseReport') {
    pageSlug = params ? `case-report/${params}` : 'case-report';
    return `/${slug}/${pageSlug}`;
  }
  if (fn === 'Open_Person') {
    pageSlug = params ? `person/${params}` : 'person';
    return `/${slug}/${pageSlug}`;
  }
  if (fn === 'Open_Vehicle') {
    pageSlug = params ? `vehicle/${params}` : 'vehicle';
    return `/${slug}/${pageSlug}`;
  }
  if (fn === 'Open_Business') {
    pageSlug = params ? `business/${params}` : 'business';
    return `/${slug}/${pageSlug}`;
  }
  if (fn === 'Open_ClientGroup') {
    pageSlug = params ? `client/${params}` : `client/new`;
    return `/${slug}/${pageSlug}`;
  }
  if (fn === 'TourStopCompletion') {
    pageSlug = params ? `tour-stop-completion/${params}` : 'search';
    return `/${slug}/${pageSlug}`;
  }
  if (fn === 'Open_AdminClientGroup') {
    pageSlug = params ? `client-group/${params}` : 'client-group';
    return `/admin/${pageSlug}`;
  }
  if (fn === 'Open_CitationForm') {
    pageSlug = `citation/${params || 'new'}`;
    return `/${slug}/${pageSlug}`;
  }
  if (fn === 'NPSD_MapByAddress') {
    return `/${slug}/map?address=${encodeURIComponent(params || '')}`;
  }
  if (fn && fn.endsWith('_MapById')) {
    return `/${slug}/map?callCardId=${encodeURIComponent(params || '')}`;
  }
  if (fn === 'showCompleteTourStopsForGuardTour' && params) {
    const parts = params.split('|');
    return `/${slug}/guard-tours/${parts[0]}/stops/${parts[1] || ''}`;
  }
  if (fn && (fn.endsWith('_ShowCallCard') || fn.endsWith('_CallCard'))) {
    pageSlug = params ? `call-card/${params}` : 'call-card';
    return `/${slug}/${pageSlug}`;
  }
  if (fn && fn.endsWith('_Selected')) {
    return `/${slug}/home`;
  }
  if (fn && fn.endsWith('_CallCardSop')) {
    return `/${slug}/sop/${params || ''}`;
  }

  // Payment special cases
  if (fn === 'ADMIN_PaymentManagement') {
    return '/admin/payment-management';
  }
  if (section === 'PAYMENT') {
    const tabSlug = tabSlugs[tab] || tab.toLowerCase();
    return `/payment/${tabSlug}`;
  }

  // Admin-specific routes (non-multiModule admin tabs)
  if (section === 'ADMIN' && !multiModule) {
    const tabSlug = tabSlugs[tab] || tab.toLowerCase();
    return `/admin/${tabSlug}`;
  }

  // Default: section/tab-slug with optional call card params
  const tabSlug = tabSlugs[tab] || tab.toLowerCase();
  if (tab === 'NewCallCard' || tab === 'SelfInitiate') {
    pageSlug = params ? `call-card/${params}` : 'call-card';
    return `/${slug}/${pageSlug}`;
  }

  if (tab === 'UserProfile' && params) {
    return `/${slug}/user-profile/${params}`;
  }

  return `/${slug}/${tabSlug}`;
}

/**
 * Convert a legacy query-string URL to a hash path.
 * e.g. "/?sectionName=CAD&tab=InProgress&function=CAD_InProgress" → "/cad/in-progress"
 */
export function legacyUrlToHashPath(queryString: string): string | null {
  // Parse the query string — strip any leading pathname (e.g. "/?sectionName=..." → "?sectionName=...")
  const qsStart = queryString.indexOf('?');
  const qs = qsStart >= 0 ? queryString.substring(qsStart) : '?' + queryString;
  const params = new URLSearchParams(qs);

  if (params.get('signout') !== null || queryString === 'signout') {
    return '/signout';
  }

  const section = params.get('sectionName');
  if (!section) return null;

  const tab = params.get('tab') || undefined;
  const fn = params.get('function') || undefined;
  const multiModule = params.get('multiModule') === 'true' || params.get('multiModule') === 'True';
  const routeParams = params.get('params') || undefined;

  return buildHashPath({
    section,
    tab,
    fn,
    multiModule,
    params: routeParams,
  });
}
